'use client';

import Link from 'next/link';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';

/**
 * "Works with your AI" — the MCP connector on the landing page (MCP wave PR4).
 *
 * Narrative slot: directly after AfterTheCallSection ("every check-in becomes
 * searchable knowledge") — this is that knowledge queried from the AI your
 * team already uses. Plain background (the neighbor is bg-card).
 *
 * COPY HONESTY (the 4K-vaporware rule, enforced by the PR3 review):
 *  • "Claude, ChatGPT & any MCP-enabled assistant" — never Gemini as a flat
 *    peer (the consumer Gemini app has no custom-connector flow; only the CLI).
 *  • Team plan, owners & admins — stated in the section, not buried.
 *  • Read-only + permission-following — the two promises the backend actually
 *    enforces per call (fail-closed re-derivation).
 * NOT named "The connector" — that phrase already means Tessa's intro-making
 * in AfterTheCallSection.
 *
 * The chat panel is a hand-built HTML mini-mock (house rule: never
 * screenshots); internals aria-hidden with a role="img" label on the panel.
 */
export default function WorksWithYourAiSection() {
  return (
    <section className="py-20">
      <div className="max-w-[1200px] mx-auto px-8">
        <div className="text-center mb-13">
          <div className="landing-section-label">Works with your AI</div>
          <h2 className="text-[38px] font-bold tracking-[-1.5px] mb-3.5 text-foreground">
            Ask your check-ins anything — from Claude or ChatGPT.
          </h2>
          <p className="text-[17px] text-muted-foreground max-w-[540px] mx-auto">
            Connect Apprendo to the AI assistant your team already uses. It can search your
            recordings, transcripts, routines and insights — and only ever sees what you can see.
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.8 }}
          className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center"
        >
          {/* Text column */}
          <div>
            <ul className="space-y-3 mb-8">
              {[
                'Works with Claude, ChatGPT & any MCP-enabled assistant',
                'Read-only — your assistant can search, never change anything',
                'Follows your own Apprendo permissions, workspace by workspace',
                'Included with the Team plan and higher — builders connect once in Settings',
              ].map((line) => (
                <li key={line} className="flex items-start gap-2.5 text-[14px] text-foreground">
                  <Check className="w-4 h-4 text-[#10B981] flex-shrink-0 mt-0.5" />
                  {line}
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap items-center gap-3">
              <Link
                href="/login?signup=true&next=%2Fdashboard%2Fsettings%2Fconnectors"
                className="inline-flex items-center justify-center gap-2 bg-[#EF4444] text-white px-6 py-3 rounded-full text-[15px] font-semibold shadow-[0_4px_14px_rgba(239,68,68,0.3)] hover:bg-[#DC2626] hover:-translate-y-0.5 transition-all"
              >
                Connect your AI
              </Link>
              <Link
                href="#pricing"
                className="inline-flex items-center justify-center px-6 py-3 border border-border text-foreground rounded-full text-[15px] font-semibold hover:border-foreground transition-all"
              >
                See Team pricing
              </Link>
            </div>
          </div>

          {/* Chat mock panel — hand-built HTML, never a screenshot. */}
          <div
            role="img"
            aria-label="Example: asking your AI assistant a question and getting an answer sourced from an Apprendo check-in"
            className="relative overflow-hidden rounded-[16px] border border-border min-h-[280px] flex items-center justify-center p-6 bg-gradient-to-br from-[#3B82F6]/15 via-[#8B5CF6]/10 to-transparent"
          >
            <div
              aria-hidden="true"
              className="absolute -top-10 -right-10 w-48 h-48 rounded-full opacity-40 blur-[50px]"
              style={{ background: '#3B82F6' }}
            />
            <div
              aria-hidden="true"
              className="absolute -bottom-12 -left-8 w-40 h-40 rounded-full opacity-20 blur-[60px]"
              style={{ background: '#8B5CF6' }}
            />
            <div
              aria-hidden="true"
              className="relative w-full max-w-[380px] rounded-xl border border-border bg-card p-4 shadow-lg space-y-3"
            >
              <div
                className="text-[10px] uppercase tracking-[1.5px] text-muted-foreground"
                style={{ fontFamily: 'var(--font-space-mono)' }}
              >
                In your AI assistant
              </div>
              <div className="flex justify-end">
                {/* blue-600, not brand #3B82F6: white 13px text needs ≥4.5:1
                    (white on #3B82F6 is 3.68:1; on #2563EB it's 5.17:1) — the
                    same review standard the HowItWorks mocks carry. */}
                <p className="bg-[#2563EB] text-white text-[13px] leading-[1.6] rounded-2xl rounded-br-sm px-3.5 py-2 max-w-[80%]">
                  What&rsquo;s blocking the team this week?
                </p>
              </div>
              <div className="flex justify-start">
                <div className="bg-muted/60 border border-border rounded-2xl rounded-tl-sm px-3.5 py-2.5 max-w-[92%]">
                  <p className="text-[13px] text-foreground leading-[1.6]">
                    Three blockers from Tuesday&rsquo;s check-in: the Meridian renewal needs
                    sign-off by Thursday (Maria), the new hire is still waiting on staging access,
                    and the Q3 pricing deck review slipped.
                  </p>
                  <p
                    className="mt-2 text-[10px] uppercase tracking-[1.5px] text-muted-foreground"
                    style={{ fontFamily: 'var(--font-space-mono)' }}
                  >
                    Source: Weekly Sales Check-in
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
